/**
 * Cross-window logout for the v2 SPA.
 *
 * Auth0 keeps `isAuthenticated` in memory per tab even though the token cache
 * lives in `localStorage`. Signing out in one window therefore leaves other
 * same-origin windows (tabs, extra browser windows, installed PWA) still
 * signed in until we tell them to drop the local session.
 *
 * Other browser products (Chrome vs Firefox) do not share storage; those
 * sessions stay independent.
 */
import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import { isNativePlatform } from "@/lib/native";

import { authReturnTo } from "./nativeAuth";

export const AUTH_LOGOUT_CHANNEL = "twido-v2-auth-session";
export const AUTH_LOGOUT_STORAGE_KEY = "twido.auth.logout";
export const AUTH0_SDK_CACHE_PREFIX = "@@auth0spajs@@";

type AuthLogoutMessage = { type: "logout" };

function isAuthLogoutMessage(data: unknown): data is AuthLogoutMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as AuthLogoutMessage).type === "logout"
  );
}

/** True when the Auth0 SPA SDK still has a token cache in localStorage. */
export function hasAuth0SdkCache(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(AUTH0_SDK_CACHE_PREFIX)) return true;
    }
  } catch {
    return false;
  }
  return false;
}

/**
 * `storage` events from another same-origin window that mean this tab should
 * drop its in-memory Auth0 session.
 */
export function isRemoteLogoutStorageEvent(event: StorageEvent): boolean {
  if (event.storageArea && event.storageArea !== localStorage) return false;

  if (event.key === AUTH_LOGOUT_STORAGE_KEY) {
    return event.newValue !== null;
  }

  // Another tab called `localStorage.clear()` (or the user wiped site data).
  if (event.key === null) {
    return true;
  }

  if (
    event.key.startsWith(AUTH0_SDK_CACHE_PREFIX) &&
    event.newValue === null &&
    !hasAuth0SdkCache()
  ) {
    return true;
  }

  return false;
}

function postLogoutChannel(): void {
  if (typeof BroadcastChannel === "undefined") return;
  try {
    const channel = new BroadcastChannel(AUTH_LOGOUT_CHANNEL);
    channel.postMessage({ type: "logout" } satisfies AuthLogoutMessage);
    channel.close();
  } catch {
    // Private mode / unsupported — storage event below is the fallback.
  }
}

/**
 * Tell other same-origin windows this tab is signing out. Call immediately
 * before Auth0's redirect so the signal is delivered while this document is
 * still alive.
 */
export function signalAuthLogout(): void {
  postLogoutChannel();
  try {
    localStorage.setItem(
      AUTH_LOGOUT_STORAGE_KEY,
      `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    );
  } catch {
    // Private mode / quota — BroadcastChannel may still have fired.
  }
}

/** Subscribe to logout signals from other same-origin windows. */
export function subscribeAuthLogout(onLogout: () => void): () => void {
  let channel: BroadcastChannel | null = null;

  const onMessage = (event: MessageEvent) => {
    if (isAuthLogoutMessage(event.data)) onLogout();
  };

  if (typeof BroadcastChannel !== "undefined") {
    try {
      channel = new BroadcastChannel(AUTH_LOGOUT_CHANNEL);
      channel.addEventListener("message", onMessage);
    } catch {
      channel = null;
    }
  }

  const onStorage = (event: StorageEvent) => {
    if (isRemoteLogoutStorageEvent(event)) onLogout();
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }

  return () => {
    channel?.removeEventListener("message", onMessage);
    channel?.close();
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

/**
 * When another window signs out, send this window through Auth0 logout too.
 * A local-only clear would race the initiating `/v2/logout` and let silent
 * SSO sign the user back in.
 */
export function useAuthSessionSync(): void {
  const { isAuthenticated, isLoading, logout } = useAuth0();
  const remoteLogoutLock = useRef(false);
  const sawSdkCache = useRef(false);

  useEffect(() => {
    if (isAuthenticated) remoteLogoutLock.current = false;
    if (isAuthenticated && hasAuth0SdkCache()) sawSdkCache.current = true;
    if (!isAuthenticated && !isLoading) sawSdkCache.current = false;
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    if (isNativePlatform()) return;

    const applyRemoteLogout = () => {
      if (remoteLogoutLock.current) return;
      if (isLoading || !isAuthenticated) return;
      remoteLogoutLock.current = true;
      logout({
        logoutParams: { returnTo: authReturnTo() },
      });
    };

    const unsubscribe = subscribeAuthLogout(applyRemoteLogout);

    const onResume = () => {
      if (isLoading || !isAuthenticated) return;
      if (sawSdkCache.current && !hasAuth0SdkCache()) applyRemoteLogout();
    };

    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("focus", onResume);
    window.addEventListener("pageshow", onResume);

    return () => {
      unsubscribe();
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("focus", onResume);
      window.removeEventListener("pageshow", onResume);
    };
  }, [isAuthenticated, isLoading, logout]);
}
